import { useContext, useState } from 'react'
import { AxiosContext } from '@/context/Axios'
import useAppStore from '@/store/useAppStore'
import { ITopicCacheSlice, SearchType, Topic, TopicListFilter } from '@/types'
import {
  DEFAULT_TOPIC_APPLICATION,
  PrivacyType,
  SHOW_WHITE_LIST_ONLY,
  TopicType,
  TopicTypeAndApplicationDic,
  whiteListFilterFn,
} from '@/utils/constants'
import { TopicsCacheKey } from '@/utils/cacheKeys'

interface BaseTopicsParams {
  connID?: string
  searchType?: SearchType
  pageNo?: number
  privacyType?: PrivacyType
  topicType?: TopicType
  filter?: TopicListFilter
}

export const useTopicsListViewModel = (cacheKey?: TopicsCacheKey) => {
  const cachedEntry = useAppStore((state) =>
    cacheKey ? (state[cacheKey] as ITopicCacheSlice) : undefined,
  )
  const setCacheEntry = useAppStore((state) =>
    cacheKey ? (state[cacheKey] as ITopicCacheSlice)?.setCacheEntry : undefined,
  )

  const [localTopicsList, setLocalTopicsList] = useState<Topic[] | null>(null)
  const [localPagination, setLocalPagination] = useState<any>()
  const [localCanLoadNextPage, setLocalCanLoadNextPage] = useState(false)
  const [localLastParams, setLocalLastParams] = useState<BaseTopicsParams | null>(null)

  const [loading, setLoading] = useState(false)
  const [loadingNextPage, setLoadingNextPage] = useState(false)
  const axios = useContext(AxiosContext)

  const topicsList = cacheKey ? cachedEntry?.topicsList || null : localTopicsList
  const pagination = cacheKey ? cachedEntry?.pagination : localPagination
  const canLoadNextPage = cacheKey ? cachedEntry?.canLoadNextPage || false : localCanLoadNextPage
  // Params that produced the CURRENT cached list — not necessarily this
  // render's `baseParams`, which may have since changed (e.g. a filter edit).
  const lastParams: BaseTopicsParams | null = cacheKey
    ? (cachedEntry?.lastParams ?? null)
    : localLastParams

  const getTopics = async (params: BaseTopicsParams) => {
    setLoading(true)
    if (params.pageNo != 1) {
      setLoadingNextPage(true)
    }

    const { topicType } = params

    const response = await axios.api.post('ajax/topics', {
      requestType: 'topicList',
      list: 'mini',
      format: 'json',
      searchType: 1,
      interaction: params.privacyType,
      topicApplication:
        TopicTypeAndApplicationDic[topicType?.toString()!] || DEFAULT_TOPIC_APPLICATION,
      ...params,
    })
    setLoading(false)
    setLoadingNextPage(false)
    let filteredResult: Topic[] = response.data?.myTopics || []
    if (SHOW_WHITE_LIST_ONLY) {
      filteredResult = filteredResult.filter(whiteListFilterFn)
    }

    const prevList = params.pageNo == 1 ? [] : topicsList || []
    const newList = [...prevList, ...filteredResult]
    const newPagination = response.data.pagination
    const newCanLoadNextPage = newPagination?.currentPage != newPagination?.totalPages

    if (cacheKey && setCacheEntry) {
      setCacheEntry({
        topicsList: newList,
        pagination: newPagination,
        canLoadNextPage: newCanLoadNextPage,
        lastParams: params,
      })
    } else {
      setLocalTopicsList(newList)
      setLocalPagination(newPagination)
      setLocalCanLoadNextPage(newCanLoadNextPage)
      setLocalLastParams(params)
    }
  }

  // Convenience wrapper for the common case: fetch using the params this
  // hook was created with (page 1 typically, or explicit pageNo).

  const loadNextPageData = () => {
    if (canLoadNextPage && lastParams) {
      getTopics({ ...lastParams, pageNo: (pagination?.currentPage || 1) + 1 })
    }
  }

  return {
    topicsList: topicsList || [],
    pagination,
    loading,
    loadingNextPage,
    getTopics,
    canLoadNextPage,
    loadNextPageData,
    topicsCount: pagination ? pagination.topicsCount || 0 : null,
  }
}
